import React from 'react';
import { StyleSheet, Text, View, Platform } from 'react-native';
import i18n from 'i18n-js';
import { useSelector } from 'react-redux';

import { moderateScale, isPhone, formatTableTime, Popup, getLinkingModalConfig } from '@helpers';
import { fullWidth, scale } from '@components/frame';
import ColorWrapper from '@styles/colors';
import { font } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import Svg from '@styles/icons/svg';
import Button from '@styles/buttons';
import NCFileType from '@components/common/NCFileType';
import { setNcFileData } from '@reducers/details';
import { useAppDispatch } from '@store/hooks/useAppDispatch';

import Image from '@v2/context/Image/context';
import { LevelSubScreen } from '@v2/pages/common/enums';
import { getStatusText, renderStatusIcon, triggerImageUpload, formatScheduledTime, getImageSize, getItemStyle, getStatusFlags, buildImageData, buildPartInfoText } from '@components/search/ncFiles/helpers';
import { SCREEN_BORDER_RADIUS, TILE_CONFIG, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import Empty from '@components/common/Empty';

const Item = ({ isEmpty, itemSize = TILE_CONFIG.BASE_WIDTH, item, index, C, isSmallMode, goToDetails, generateImageURL, isSelected, imageHandling, attachMode, attachImageId }) => {
  const dispatch = useAppDispatch();
  const ncFileData = useSelector((state) => state.details.ncFileData);

  const style = getItemStyle(isEmpty, itemSize, isSmallMode);
  const imgSize = getImageSize(itemSize);
  const { isUnknown, isIgnored, isChange, isSetup, isLinked, showOverlay } = getStatusFlags(item.ncFileStatus);
  const imageData = buildImageData(item, imgSize, () => goToDetails(item, LevelSubScreen.DOCUMENTS), attachMode);

  return isEmpty ? (
    <View style={[styles.itemComponent, style]} />
  ) : (
    <TouchableElement testID={TEST_IDS.searchScreen.ncFiles.itemTile} onPress={() => goToDetails(item, isSmallMode ? LevelSubScreen.DOCUMENTS : undefined)} hitSlop={0}>
      {({ pressColor, boxStyle }) => (
        <View
          style={[
            styles.itemComponent,
            style,
            boxStyle,
            isSelected && { borderWidth: scale(1), borderColor: C('System/Light/Blue') },
            {
              backgroundColor: C(pressColor('Background/Light/Field [mobile]')),
              borderRadius: scale(SCREEN_BORDER_RADIUS),
            },
          ]}
        >
          <View
            style={[
              imgSize,
              {
                backgroundColor: C(isUnknown || showOverlay ? 'Background/Light/Tile No Picture' : 'Background / Light / System'),
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: scale(SCREEN_BORDER_RADIUS),
                overflow: 'hidden',
              },
            ]}
          >
            {isUnknown ? (
              // unknown status and no image
              <TouchableElement onPress={() => triggerImageUpload(item)} hitSlop={0}>
                {({ boxStyle, hovered }) => (
                  <View style={{ justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <View
                      style={[
                        boxStyle,
                        {
                          justifyContent: 'center',
                          alignItems: 'center',
                          paddingVertical: scale(12),
                          paddingHorizontal: scale(16),
                          borderRadius: scale(12),
                          backgroundColor: hovered ? C('Button/Light/Secondary hover') : 'transparent',
                        },
                      ]}
                    >
                      <Svg
                        icon="addPicture"
                        color={'Label/Light/Tertiary'}
                        width={scale(isSmallMode ? 38 : 72)}
                        height={scale(isSmallMode ? 38 : 72)}
                        C={C}
                      />
                    </View>
                  </View>
                )}
              </TouchableElement>
            ) : (
              <View
                style={{
                  position: 'relative',
                  height: '100%',
                  width: '100%',
                }}
              >
                <Image {...imageData} />
                {showOverlay && (
                  // ignored/Change status
                  <View
                    style={[
                      StyleSheet.absoluteFill,
                      {
                        backgroundColor: C('Background/Light/Tile No Picture'),
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        padding: scale(12),
                        borderRadius: scale(SCREEN_BORDER_RADIUS),
                      },
                    ]}
                  >
                    <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary'), textAlign: 'left' }]}>
                      {i18n.t(`marked as ${isIgnored ? 'Ignored' : isSetup ? 'Setup' : 'Change'}${item.ncFileStatusSetBy ? ' by' : ''}`)}
                    </Text>
                    {item.ncFileStatusSetBy && (
                      <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary'), textAlign: 'left', marginTop: scale(4) }]}>
                        {item.ncFileStatusSetBy}
                      </Text>
                    )}
                    <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary'), textAlign: 'left', marginTop: scale(4) }]}>
                      {i18n.t('on')} {item.ncFileStatusSetOn ? formatTableTime(item.ncFileStatusSetOn) : ''}
                    </Text>
                  </View>
                )}
              </View>
            )}
          </View>
          {!isSmallMode && (
            <View>
              <View style={styles.itemImgMarksWrapper}>
                {item.new == 1 ? (
                  <View style={[styles.itemStatusMark, { backgroundColor: C('Background / Light / New flag') }]}>
                    <Text style={[styles.itemStatusMarkText, { color: C('Label / Light / AlwaysDark Primary') }, font.Small]}>Nieuw</Text>
                  </View>
                ) : (
                  <View />
                )}
              </View>
              <View style={styles.itemTextBlock}>
                {/* Row 1: ncFileName */}
                <View style={styles.lineContainer}>
                  <Text style={[styles.itemHeader, { color: C('Label / Light / Primary') }, font.Headline]} numberOfLines={1} ellipsizeMode={'middle'}>
                    {item.ncFileName}
                    <Empty />
                  </Text>
                </View>

                {/* Row 2: Status-specific */}
                {isLinked ? (
                  <View style={{ marginTop: scale(2), flexDirection: 'row' }}>
                    <Text style={[{ color: C('Label/Light/Tertiary') }, font['Subhead 1']]} numberOfLines={1}>
                      {item.partDescription || item.memo}
                      <Empty />
                    </Text>
                  </View>
                ) : (
                  <View style={{ marginTop: scale(2), flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingRight: 9 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                      <View style={{ marginRight: scale(4) }}>{renderStatusIcon(item.ncFileStatus)}</View>
                      <Text style={[{ color: C('Label/Light/Tertiary') }, font['Subhead 1']]}>
                        {getStatusText(item.ncFileStatus)}
                      </Text>
                    </View>
                    {isUnknown && (
                      <Button
                        {...{
                          testID: TEST_IDS.searchScreen.ncFiles.updateNcFileTypeButton,
                          variant: 'S',
                          type: 'Secondary',
                          onPress: () => {
                            if (ncFileData?.data?.ncFileId !== item?.ncFileId) {
                              dispatch(setNcFileData(item));
                            }
                            Popup({
                              Component: (p) => (
                                <NCFileType
                                  {...{
                                    ...p,
                                    selectedItem: {
                                      machineStatus: item,
                                    },
                                  }}
                                />
                              ),
                              ...getLinkingModalConfig(scale, fullWidth),
                            });
                          },
                        }}
                      >
                        {i18n.t('Update')}
                      </Button>
                    )}
                  </View>
                )}

                {/* Row 3: Status-specific */}
                {isLinked ? (
                  <View style={{ marginTop: scale(2), flexDirection: 'row' }}>
                    <Text style={[{ color: C('Label/Light/Tertiary') }, font['Subhead 1']]} numberOfLines={1}>
                      {buildPartInfoText(item)}
                      <Empty />
                    </Text>
                  </View>
                ) : (
                  <View style={{ marginTop: scale(2), flexDirection: 'row' }}>
                    <Text style={[{ color: C('Label/Light/Tertiary') }, font['Subhead 1']]} numberOfLines={1}>
                      {i18n.t('First run')} {item.firstRanOn ? formatTableTime(item.firstRanOn) : ''} {i18n.t('on')} {item.firstRanByMachineName || ''}
                      <Empty />
                    </Text>
                  </View>
                )}

                {/* Row 4: Scheduled per pcs (all statuses) */}
                <View style={{ marginTop: scale(2), flexDirection: 'row' }}>
                  <Text style={[{ color: C('Label/Light/Tertiary') }, font['Subhead 2']]} numberOfLines={1}>
                    {i18n.t('Scheduled per pcs')}: {formatScheduledTime(item)}
                    <Empty />
                  </Text>
                </View>
              </View>
            </View>
          )}
        </View>
      )}
    </TouchableElement>
  );
};

export default ColorWrapper(Item);

const styles = StyleSheet.create({
  lineContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  itemComponent: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemImgMarksWrapper: {
    display: 'flex',
    paddingLeft: 9,
    paddingRight: 9,
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 21,
    position: 'relative',
    top: -27,
    flexDirection: 'row',
  },
  itemStatusMark: {
    paddingLeft: 1,
    paddingRight: 1,
    height: 15,
    borderRadius: 2,
    marginTop: Platform.OS === 'android' ? 0 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemStatusMarkText: {
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: moderateScale(9.5),
    marginTop: Platform.OS === 'android' ? -1 : 1,
    letterSpacing: moderateScale(0.02),
    textTransform: 'uppercase',
    paddingLeft: 3,
    paddingRight: 3,
  },
  itemDateIcon: {
    height: 13,
    width: 13,
    marginLeft: Platform.OS === 'android' ? 1 : 0,
    marginTop: 1,
  },
  itemDateMark: {
    display: 'flex',
    flexDirection: 'row',
    borderRadius: 11,
    height: 22,
    paddingLeft: 8,
    paddingRight: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  itemDate: {
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: moderateScale(16),
    lineHeight: moderateScale(19),
    letterSpacing: moderateScale(-0.3),
    marginLeft: 4,
  },

  itemTextBlock: {
    paddingLeft: 9,
    paddingTop: 7,
    position: 'relative',
    top: -21,
  },
  itemHeader: {
    display: 'flex',
    fontFamily: Platform.OS === 'android' ? 'Roboto-Medium' : 'System',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: !isPhone ? moderateScale(17) : 18,
    lineHeight: moderateScale(20),
    letterSpacing: moderateScale(0.02),
  },
  itemHeaderSize: {
    marginLeft: 7,
  },
  itemLightText: {
    fontFamily: Platform.OS === 'android' ? 'Roboto' : 'System',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: !isPhone ? moderateScale(15) : 16,
    lineHeight: moderateScale(18),
    letterSpacing: moderateScale(0.01),
  },
  itemDescription: {
    marginTop: 5,
  },
  itemPart: {
    marginTop: 5,
  },
  itemDrawing: {
    marginTop: 5,
  },
});
